import { useState, useEffect, useCallback } from 'react';
import {
  listFirmUsers,
  createFirmUser,
  deactivateFirmUser,
  reactivateFirmUser,
  resetFirmUserPassword,
  readAuditorEngagements,
} from './grcClient.js';
import FirmDirectoryProfile from './FirmDirectoryProfile.jsx';

const ROLES = [
  { id: 'firm-admin', label: 'Firm Admin', desc: 'Manages users and firm settings' },
  { id: 'lead-auditor', label: 'Lead Auditor', desc: 'Leads engagement teams' },
  { id: 'quality-reviewer', label: 'Quality Reviewer', desc: 'Reviews and signs off engagements' },
  { id: 'staff-auditor', label: 'Staff Auditor', desc: 'Works on assigned engagements' },
];

const ROLE_COLOR = {
  'firm-admin': '#a78bfa',
  'lead-auditor': '#2dd4bf',
  'quality-reviewer': '#38bdf8',
  'staff-auditor': '#94a3b8',
};

function fmtDate(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Role badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const color = ROLE_COLOR[role] || '#94a3b8';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        padding: '2px 8px',
        borderRadius: 99,
        fontFamily: 'var(--font-mono)',
        color,
        background: `${color}18`,
        border: `1px solid ${color}40`,
      }}
    >
      {ROLES.find((r) => r.id === role)?.label ?? role}
    </span>
  );
}

// ── Add user modal ────────────────────────────────────────────────────────────

function AddUserModal({ session, onSave, onClose }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('staff-auditor');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const handleSave = async () => {
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await onSave({
        firmId: session.firmId,
        email: email.trim(),
        displayName: displayName.trim(),
        role,
        password: password.trim() || undefined,
      });
      setCreated(result);
    } catch (e) {
      setError(e.message || 'Failed to create user.');
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: '28px 32px',
            width: 440,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
            User Added
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg3)', marginBottom: 16 }}>
            <strong style={{ color: 'var(--fg)' }}>{created.displayName}</strong> can now log in
            using firm slug{' '}
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>
              {session.firmSlug}
            </span>
            .
          </div>
          {created.generatedPassword && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--fg3)',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Temporary password
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--accent)',
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '10px 14px',
                  letterSpacing: '0.08em',
                }}
              >
                {created.generatedPassword}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 6 }}>
                Copy this now — it will not be shown again.
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '28px 32px',
          width: 440,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 20 }}>
          Add Team Member
        </div>

        {[
          { label: 'Email', value: email, set: setEmail, placeholder: 'auditor@yourfirm.com' },
          {
            label: 'Display name',
            value: displayName,
            set: setDisplayName,
            placeholder: 'Jane Smith',
          },
          {
            label: 'Password',
            value: password,
            set: setPassword,
            placeholder: 'Leave blank to auto-generate',
            type: 'password',
          },
        ].map((f) => (
          <div key={f.label} style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--fg3)',
                marginBottom: 5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {f.label}
            </div>
            <input
              className="input"
              type={f.type || 'text'}
              value={f.value}
              placeholder={f.placeholder}
              onChange={(e) => f.set(e.target.value)}
            />
          </div>
        ))}

        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg3)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Role
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ROLES.map((r) => (
              <label
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${role === r.id ? ROLE_COLOR[r.id] + '60' : 'var(--border)'}`,
                  background: role === r.id ? `${ROLE_COLOR[r.id]}0d` : 'transparent',
                }}
              >
                <input
                  type="radio"
                  name="role"
                  value={r.id}
                  checked={role === r.id}
                  onChange={() => setRole(r.id)}
                  style={{ accentColor: ROLE_COLOR[r.id] }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: role === r.id ? ROLE_COLOR[r.id] : 'var(--fg)',
                    }}
                  >
                    {r.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg3)' }}>{r.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 14 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Adding…' : 'Add Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reset password modal ──────────────────────────────────────────────────────

function ResetPasswordModal({ user, session, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null); // null | { generatedPassword? }

  const handleReset = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await resetFirmUserPassword({
        userId: user.id,
        firmId: session.firmId,
        newPassword: newPassword.trim() || undefined,
      });
      setDone({ generatedPassword: res.temporaryPassword });
    } catch (e) {
      setError(e.message || 'Failed to reset password.');
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
    >
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '28px 32px',
          width: 400,
        }}
      >
        {done ? (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
              Password Reset
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--fg3)',
                marginBottom: done.generatedPassword ? 16 : 20,
              }}
            >
              {user.displayName}'s password has been reset.{' '}
              {done.generatedPassword
                ? 'Share the temporary password below with them securely.'
                : 'They can now sign in with their new password.'}
            </div>
            {done.generatedPassword && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--fg3)',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Temporary password
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'var(--accent)',
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    letterSpacing: '0.08em',
                  }}
                >
                  {done.generatedPassword}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 6 }}>
                  Copy this now — it will not be shown again.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>
              Reset Password
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20 }}>
              {user.displayName} · {user.email}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--fg3)',
                  marginBottom: 5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                New password
              </div>
              <input
                className="input"
                type="password"
                value={newPassword}
                placeholder="Leave blank to auto-generate"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            {error && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 14 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleReset} disabled={saving}>
                {saving ? 'Resetting…' : 'Reset'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Which clients one auditor can currently reach (GACA-04).
 *
 * The reverse of the engagement team panel, and the view a firm-admin actually
 * needs: "who can see this client's data" is only answerable per-auditor.
 * Loaded on demand — a firm with many auditors should not fan out one request
 * per row on every render of the Team page.
 */
function AuditorClientAccess({ user }) {
  const [open, setOpen] = useState(false);
  const [engagements, setEngagements] = useState(null);
  const [error, setError] = useState('');

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || engagements) return;
    try {
      const data = await readAuditorEngagements(user.id);
      setEngagements(data.engagements || []);
    } catch (e) {
      setError(e.message || 'Could not load this auditor’s client access');
    }
  };

  return (
    <div style={{ padding: '0 18px 12px' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ fontSize: 11 }}
        onClick={toggle}
        aria-expanded={open}
      >
        {open ? 'Hide client access' : 'Client access'}
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          {!error && engagements === null && (
            <div style={{ fontSize: 12, color: 'var(--fg3)' }}>Loading…</div>
          )}
          {!error && engagements?.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--fg3)' }}>
              This auditor is not on any engagement and can reach no client data.
            </div>
          )}
          {!error && engagements?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {engagements.map((e) => (
                <li
                  key={e.engagementId}
                  style={{ fontSize: 12, color: 'var(--fg2)', marginBottom: 3 }}
                >
                  <span style={{ color: 'var(--fg)' }}>{e.clientName}</span>
                  <span style={{ color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
                    {' '}
                    · {e.role}
                    {e.tryggrSlug ? ' · workspace linked' : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function GrcFirmSettings({ session }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [actionUser, setActionUser] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listFirmUsers(session.firmId);
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message || 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  }, [session.firmId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddUser = async (fields) => {
    const res = await createFirmUser(fields);
    load();
    return { ...res.user, generatedPassword: res.temporaryPassword };
  };

  const handleToggleActive = async (user) => {
    if (user.id === session.id) return;
    setActionUser(user.id);
    setActionBusy(true);
    try {
      if (user.isActive) {
        if (!window.confirm(`Deactivate ${user.displayName}? They will lose access immediately.`)) {
          setActionBusy(false);
          setActionUser(null);
          return;
        }
        await deactivateFirmUser({ userId: user.id, firmId: session.firmId });
      } else {
        await reactivateFirmUser({ userId: user.id, firmId: session.firmId });
      }
      load();
    } catch (e) {
      setError(e.message || 'Action failed.');
    } finally {
      setActionBusy(false);
      setActionUser(null);
    }
  };

  const activeUsers = users.filter((u) => u.isActive);
  const inactiveUsers = users.filter((u) => !u.isActive);

  const UserRow = ({ user }) => {
    const isSelf = user.id === session.id;
    const busy = actionUser === user.id && actionBusy;
    return (
      <div style={{ borderBottom: '1px solid var(--border)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 160px 140px 120px 120px',
            alignItems: 'center',
            padding: '14px 18px',
            opacity: user.isActive ? 1 : 0.5,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
                {user.displayName}
              </span>
              <RoleBadge role={user.role} />
              {isSelf && (
                <span style={{ fontSize: 10, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
                  you
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)' }}>{user.email}</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg3)' }}>
            Last login: {fmtDate(user.lastLoginAt)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
            {fmtDate(user.createdAt)}
          </div>
          <div>
            {!isSelf && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 11 }}
                onClick={() => setResetTarget(user)}
              >
                Reset password
              </button>
            )}
          </div>
          <div>
            {!isSelf && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, color: user.isActive ? 'var(--danger)' : 'var(--accent)' }}
                onClick={() => handleToggleActive(user)}
                disabled={busy}
              >
                {busy ? '…' : user.isActive ? 'Deactivate' : 'Reactivate'}
              </button>
            )}
          </div>
        </div>
        <AuditorClientAccess user={user} />
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 24px' }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>
          Team
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
          {session.firmName} · Manage who has access to the portal and what they can do
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Active members', value: activeUsers.length, accent: 'var(--accent)' },
          {
            label: 'Inactive members',
            value: inactiveUsers.length,
            accent: inactiveUsers.length ? 'var(--danger)' : 'var(--fg3)',
          },
        ].map(({ label, value, accent }) => (
          <div
            key={label}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '14px 20px',
              minWidth: 140,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: 'var(--fg3)',
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 6,
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: accent,
                fontFamily: 'var(--font-display)',
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Team table */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 12,
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 160px 140px 120px 120px',
            padding: '10px 18px',
            background: 'var(--bg2)',
            borderBottom: '1px solid var(--border)',
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--fg3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span>Name</span>
          <span>Last login</span>
          <span>Added</span>
          <span />
          <span />
        </div>

        {loading && (
          <div style={{ padding: '24px 18px', fontSize: 13, color: 'var(--fg3)' }}>Loading…</div>
        )}
        {error && (
          <div style={{ padding: '16px 18px', fontSize: 12, color: 'var(--danger)' }}>{error}</div>
        )}
        {!loading && users.length === 0 && (
          <div style={{ padding: '24px 18px', fontSize: 13, color: 'var(--fg3)' }}>
            No team members found.
          </div>
        )}
        {users.map((u) => (
          <UserRow key={u.id} user={u} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => setShowAddUser(true)}>
          + Add Team Member
        </button>
      </div>

      <FirmDirectoryProfile session={session} />

      {/* Modals */}
      {showAddUser && (
        <AddUserModal
          session={session}
          onSave={handleAddUser}
          onClose={() => {
            setShowAddUser(false);
          }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          session={session}
          onClose={() => {
            setResetTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
